import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const DAR: [number, number] = [39.27, -6.79];
const CAN: [number, number] = [113.33, 23.13];

const intlCities = [
  { name: '孟买 (BOM)',           v: [72.87,  19.07] },
  { name: '约翰内斯堡 (JNB)',     v: [28.23, -26.13] },
  { name: '内罗毕 (NBO)',         v: [36.82,  -1.29] },
  { name: '开普敦 (CPT)',         v: [18.60, -33.96] },
  { name: '拉各斯 (LOS)',         v: [ 3.32,   6.45] },
  { name: '卢本巴希 (FBM)',       v: [27.48, -11.67] },
  { name: '金沙萨 (FIH)',         v: [15.44,  -4.38] },
  { name: '卢萨卡 (LUN)',         v: [28.45, -15.41] },
  { name: '恩多拉 (NLA)',         v: [28.67, -12.97] },
  { name: '哈拉雷 (HRE)',         v: [31.05, -17.83] },
  { name: '布琼布拉 (BJM)',       v: [29.35,  -3.38] },
  { name: '恩德培 (EBB)',         v: [32.43,   0.04] },
  { name: '科摩罗 (HAH)',         v: [43.27, -11.53] },
  { name: '维多利亚瀑布 (VFA)', v: [25.84, -18.09] },
  { name: '🆕 塞舌尔 (SEZ)',     v: [55.52,  -4.67] },
];

const middleEastCities = [
  { name: '迪拜 (DXB)', v: [55.37, 25.25] },
  { name: '孟买 (BOM)', v: [72.87, 19.07] },
];

const futureCities = [
  { name: '马普托 (MPM)',     v: [32.57, -25.92] },
  { name: '布拉柴维尔 (BZV)', v: [15.25,  -4.26] },
  { name: '戈马 (GOM)',       v: [29.24,  -1.68] },
  { name: '伦敦 (LON)',       v: [-0.12,  51.51] },
];

const domCities = [
  { name: '桑给巴尔 (ZNZ)',   v: [39.22,  -6.13] },
  { name: '乞力马扎罗 (JRO)', v: [37.07,  -3.43] },
  { name: '阿鲁沙 (ARK)',     v: [36.63,  -3.37] },
  { name: '姆万扎 (MWZ)',     v: [32.93,  -2.46] },
  { name: '布科巴 (BKZ)',     v: [31.82,  -1.33] },
  { name: '塔波拉 (TBO)',     v: [32.83,  -5.08] },
  { name: '多多马 (DOD)',     v: [35.74,  -6.17] },
  { name: '基戈马 (TKQ)',     v: [29.60,  -4.88] },
  { name: '姆贝亚 (MBI)',     v: [33.46,  -8.91] },
  { name: '姆特瓦拉 (MYW)',   v: [40.18, -10.34] },
  { name: '松盖亚 (SGX)',     v: [35.58, -10.68] },
  { name: '伊灵加 (IRI)',     v: [35.70,  -7.77] },
  { name: '林迪 (LDI)',       v: [39.71,  -9.99] },
];

const makeLines = (cities: { v: number[] }[], from: number[]) =>
  cities.map(c => ({ coords: [from, c.v] }));

export default function RouteMap() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current);

    fetch('/world.json')
      .then(r => r.json())
      .then((worldData: any) => {
        echarts.registerMap('world', worldData);
        chart.setOption({
          backgroundColor: '#d8eef5',
          tooltip: {
            trigger: 'item',
            formatter: (p: any) => p.data?.name ? `<b>${p.data.name}</b>` : '',
          },
          legend: [{
            data: ['DAR枢纽', 'CAN广州', '中国直飞', '中东/南亚', '区域/非洲', '坦桑国内', '计划新开'],
            orient: 'vertical', right: 6, top: 6,
            textStyle: { fontSize: 11, color: '#333' },
            itemWidth: 14, itemHeight: 8, itemGap: 5,
          }],
          geo: {
            map: 'world', roam: true, zoom: 2.2, center: [35, -4],
            itemStyle: { areaColor: '#cde8b8', borderColor: '#9bbf80', borderWidth: 0.5 },
            emphasis: { itemStyle: { areaColor: '#b8d99e' }, label: { show: false } },
            label: { show: false },
          },
          series: [
            { name: '中国直飞', type: 'lines', coordinateSystem: 'geo',
              data: [{ coords: [CAN, DAR] }],
              lineStyle: { color: '#e63946', width: 2.5, opacity: 0.95, curveness: 0.2 },
              effect: { show: true, period: 4, symbol: 'arrow', symbolSize: 7, color: '#e63946' },
              zlevel: 4 },
            { name: '中东/南亚', type: 'lines', coordinateSystem: 'geo',
              data: makeLines(middleEastCities, DAR),
              lineStyle: { color: '#9b59b6', width: 2, opacity: 0.85, curveness: 0.18 },
              zlevel: 3 },
            { name: '区域/非洲', type: 'lines', coordinateSystem: 'geo',
              data: makeLines(intlCities.filter(c => !middleEastCities.find(m => m.name === c.name)), DAR),
              lineStyle: { color: '#1a6cb8', width: 1.5, opacity: 0.75, curveness: 0.15 },
              zlevel: 3 },
            { name: '坦桑国内', type: 'lines', coordinateSystem: 'geo',
              data: makeLines(domCities, DAR),
              lineStyle: { color: '#27ae60', width: 1, opacity: 0.65, curveness: 0.1 },
              zlevel: 2 },
            { name: '计划新开', type: 'lines', coordinateSystem: 'geo',
              data: makeLines(futureCities, DAR),
              lineStyle: { color: '#999', width: 1, opacity: 0.4, curveness: 0.15, type: 'dashed' },
              zlevel: 1 },
            { name: '中东/南亚', type: 'scatter', coordinateSystem: 'geo',
              data: middleEastCities.map(c => ({ name: c.name, value: c.v })),
              symbolSize: 9, itemStyle: { color: '#9b59b6', borderColor: '#fff', borderWidth: 1.5 }, zlevel: 5 },
            { name: '区域/非洲', type: 'scatter', coordinateSystem: 'geo',
              data: intlCities.filter(c => !middleEastCities.find(m => m.name === c.name)).map(c => ({ name: c.name, value: c.v })),
              symbolSize: 8, itemStyle: { color: '#1a6cb8', borderColor: '#fff', borderWidth: 1.5 }, zlevel: 5 },
            { name: '坦桑国内', type: 'scatter', coordinateSystem: 'geo',
              data: domCities.map(c => ({ name: c.name, value: c.v })),
              symbolSize: 6, itemStyle: { color: '#27ae60', borderColor: '#fff', borderWidth: 1 }, zlevel: 5 },
            { name: '计划新开', type: 'scatter', coordinateSystem: 'geo',
              data: futureCities.map(c => ({ name: c.name, value: c.v })),
              symbol: 'diamond', symbolSize: 7,
              itemStyle: { color: '#bbb', borderColor: '#fff', borderWidth: 1 }, zlevel: 5 },
            { name: 'DAR枢纽', type: 'scatter', coordinateSystem: 'geo',
              data: [{ name: '达累斯萨拉姆 DAR（枢纽）', value: DAR }],
              symbolSize: 18, itemStyle: { color: '#c0392b', borderColor: '#fff', borderWidth: 2 },
              label: { show: true, formatter: 'DAR', position: 'top', color: '#c0392b', fontSize: 11, fontWeight: 'bold' },
              zlevel: 6 },
            { name: 'CAN广州', type: 'scatter', coordinateSystem: 'geo',
              data: [{ name: '广州 CAN（中国）', value: CAN }],
              symbolSize: 15, symbol: 'pin',
              itemStyle: { color: '#e63946', borderColor: '#fff', borderWidth: 2 },
              label: { show: true, formatter: 'CAN', position: 'right', color: '#e63946', fontSize: 11, fontWeight: 'bold' },
              zlevel: 6 },
          ],
        });
      })
      .catch(() => {
        if (ref.current) {
          ref.current.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#666;font-size:13px;text-align:center;padding:20px;">⚠️ 地图数据加载失败<br><small>请刷新页面重试</small></div>';
        }
      });

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current!);
    return () => { chart.dispose(); ro.disconnect(); };
  }, []);

  return (
    <div className="w-full rounded-2xl overflow-hidden border border-border shadow-md">
      <div ref={ref} style={{ width: '100%', height: '480px', background: '#d8eef5' }} />
      <div className="bg-card border-t border-border px-4 py-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><span style={{ color: '#e63946' }}>━</span> 中国直飞</span>
        <span><span style={{ color: '#9b59b6' }}>━</span> 中东/南亚</span>
        <span><span style={{ color: '#1a6cb8' }}>━</span> 区域/非洲</span>
        <span><span style={{ color: '#27ae60' }}>━</span> 坦桑国内</span>
        <span><span style={{ color: '#aaa' }}>╌</span> 计划新开</span>
        <span className="ml-auto opacity-60">拖拽平移 · 滚轮缩放 · 悬停查看城市</span>
      </div>
    </div>
  );
}
